import { useTranslation } from 'react-i18next';
import {
  CompaniesWrapper,
  Container,
  InputWrapper,
  NoContentLabel,
  NoContentLabelWrapper,
  SelectWrapper,
  Text,
  TopWrapper,
} from './SupplierTab.styled';
import Input from 'components/Input/Input';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useDebounce } from 'utils/hooks/useDebounce';
import CompanyCard from '../../../components/CompanyCard/CompanyCard';
import { useGetCompanySuppliers } from './hooks';
import { ITEMS_PER_PAGE, SortByOptions } from '../constants';
import { ICompany } from 'types/Company.types';
import { Select } from 'components/Select/Select';
import { SortByOption } from '../types';
import { SortDirection } from 'components/Table/types';
import { setCompanyId } from 'store/Order/actions/order';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import CompanyCardSkeleton from 'components/CompanyCard/CompanyCardSkeleton/CompanyCardSkeleton';
import Pagination from 'components/Pagination/Pagination';

const SupplierTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const [page, setPage] = useState<number>(1);
  const { companyId } = useSelector((state: RootState) => state.orderInfo);

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  const [selectedSortBy, setSelectedSortBy] = useState<SortByOption>(
    SortByOption.Name
  );
  const [selectedSortDirection, setSelectedSortDirection] =
    useState<SortDirection>(SortDirection.ASC);

  const { data: companySuppliersData, isLoading: isLoadingCompanySuppliers } =
    useGetCompanySuppliers(
      page,
      ITEMS_PER_PAGE,
      debouncedSearchBy,
      selectedSortBy,
      selectedSortDirection
    );

  const handleCompanySelect = (company: ICompany) => {
    if (company.id.toString() === companyId) {
      dispatch(setCompanyId(''));
    } else {
      dispatch(setCompanyId(company.id.toString()));
    }
  };

  const renderSkeletons = () => (
    <CompaniesWrapper>
      {Array.from({ length: ITEMS_PER_PAGE }, (_, index) => (
        <CompanyCardSkeleton
          key={index}
          index={index}
          opacityCoefficient={0.1}
        />
      ))}
    </CompaniesWrapper>
  );

  return (
    <Container>
      <Text>
        {t('Select one company that you want to create the order for.')}
      </Text>

      <TopWrapper>
        <InputWrapper>
          <Input
            placeholder={t('Search')}
            width={'100%'}
            onChange={(e: any) => setSearchBy(e.target.value)}
            icon={Search}
            wrapperStyles={{ width: '100%' }}
          />
        </InputWrapper>
        <SelectWrapper>
          <Select
            width="100%"
            defaultValue={{ value: '', label: 'Sort by' }}
            name="sort-by-select"
            placeholder={t('Sort by')}
            options={SortByOptions}
            onChange={(selectedOption: any) => {
              setSelectedSortBy(
                selectedOption.value.includes('name')
                  ? SortByOption.Name
                  : SortByOption.Reference
              );
              setSelectedSortDirection(
                selectedOption.value.includes('asc')
                  ? SortDirection.ASC
                  : SortDirection.DESC
              );
            }}
          />
        </SelectWrapper>
      </TopWrapper>

      {isLoadingCompanySuppliers ? (
        renderSkeletons()
      ) : companySuppliersData?.companies.length ? (
        <CompaniesWrapper>
          {companySuppliersData.companies.map((company: ICompany) => (
            <CompanyCard
              key={company.id}
              company={company}
              isSelected={company.id.toString() === companyId}
              onSelect={handleCompanySelect}
            />
          ))}
        </CompaniesWrapper>
      ) : (
        <NoContentLabelWrapper>
          <NoContentLabel>
            {t('No suppliers available for this buyer')}
          </NoContentLabel>
        </NoContentLabelWrapper>
      )}

      {companySuppliersData?.pagination?.total > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          perPage={ITEMS_PER_PAGE}
          total={companySuppliersData?.pagination?.total || '0'}
          setPage={setPage}
        />
      )}
    </Container>
  );
};

export default SupplierTab;
